import { cva, type VariantProps } from 'class-variance-authority';
import { createElement, forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Typography primitives. All apps must compose text via Heading / Text / Label —
 * never set raw `text-xl` / `text-fg-*` classes ad-hoc in feature code. This is
 * what keeps vertical rhythm, weight, and color consistent across the app.
 */

const headingStyles = cva('font-semibold text-fg-primary tracking-tight', {
  variants: {
    level: {
      1: 'text-4xl leading-tight',
      2: 'text-3xl leading-tight',
      3: 'text-2xl leading-snug',
      4: 'text-xl leading-snug',
      5: 'text-lg',
      6: 'text-base',
    },
    align: { left: 'text-left', center: 'text-center', right: 'text-right' },
  },
  defaultVariants: { level: 2 },
});

export interface HeadingProps
  extends HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof headingStyles> {
  /** Override the rendered tag independently of visual level (defaults to h<level>). */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { className, level = 2, align, as, children, ...props },
  ref,
) {
  const tag = as ?? (`h${level}` as const);
  return createElement(
    tag,
    { ref, className: cn(headingStyles({ level, align }), className), ...props },
    children,
  );
});

const textStyles = cva('text-fg-primary', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
    },
    tone: {
      primary: 'text-fg-primary',
      secondary: 'text-fg-secondary',
      muted: 'text-fg-muted',
      inverted: 'text-fg-inverted',
      accent: 'text-accent',
      danger: 'text-danger',
      success: 'text-success',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: { left: 'text-left', center: 'text-center', right: 'text-right' },
    truncate: { true: 'truncate' },
  },
  defaultVariants: { size: 'base', tone: 'primary', weight: 'regular' },
});

export interface TextProps
  extends Omit<HTMLAttributes<HTMLElement>, 'children'>, VariantProps<typeof textStyles> {
  as?: 'p' | 'span' | 'div';
  children?: React.ReactNode;
}

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { className, size, tone, weight, align, truncate, as = 'p', children, ...props },
  ref,
) {
  return createElement(
    as,
    {
      ref,
      className: cn(textStyles({ size, tone, weight, align, truncate }), className),
      ...props,
    },
    children,
  );
});

export interface LabelProps extends HTMLAttributes<HTMLLabelElement> {
  htmlFor?: string;
}

export function Label({ className, ...props }: LabelProps) {
  return <label className={cn('text-fg-primary text-sm font-medium', className)} {...props} />;
}
